import { allUserRoles, cdAndAdminRoles, cdRoles, ROLE } from "constants/phase";
import { APP_PAGE } from "constants/v2/appConstants";
import { AI_ICON_RESTRICTED_ENTITY_IDS } from "constants/v2/clipConstants";
import { magnifiSuperAdminEntityId } from "containers/app/utils";
import { createSelector } from "reselect";

import { getEntityType } from "../../helpers";
import { selectOrganizationEntities } from "../organization/selectors";
import { selectWorkspaceEntities } from "../workspace/selectors";
import { entitySlice } from "./slice";

const selectEntityState = (state) => state[entitySlice.name];

export const selectSelectedEntityId = createSelector(selectEntityState, (entityState) => entityState.selectedEntityId);

export const selectSelectedEntityType = createSelector(selectSelectedEntityId, (entityId) => getEntityType(entityId));

export const selectEntityTypeByEntityId = createSelector(
  (state, props) => ({ entityId: props.entityId }),
  ({ entityId }) => getEntityType(entityId),
);

export const selectOrganizationAndWorkspaceEntities = createSelector(
  selectOrganizationEntities,
  selectWorkspaceEntities,
  (organizationEntities, workspaceEntities) => ({
    ...organizationEntities,
    ...workspaceEntities,
  }),
);

export const selectSelectedEntityDetails = createSelector(
  selectSelectedEntityId,
  selectOrganizationAndWorkspaceEntities,
  (entityId, organizationAndWorkspaceEntities) => {
    if (entityId === magnifiSuperAdminEntityId) {
      return {
        role: allUserRoles.superAdmin,
        displayName: "All Org",
      };
    }

    return organizationAndWorkspaceEntities[entityId] || null;
  },
);

export const selectEntityDetailsByEntityId = createSelector(
  (state, props) => ({ entityId: props.entityId }),
  selectOrganizationAndWorkspaceEntities,
  ({ entityId }, organizationAndWorkspaceEntities) => organizationAndWorkspaceEntities[entityId] || null,
);

export const selectSelectedEntityRole = createSelector(
  selectSelectedEntityDetails,
  (entityDetails) => entityDetails?.role || null,
);

const selectEntityDisplayName = (configDetails) => configDetails?.displayName || null;

export const selectSelectedEntityDisplayName = createSelector(selectSelectedEntityDetails, selectEntityDisplayName);

export const selectEntityDisplayNameByEntityId = createSelector(selectEntityDetailsByEntityId, selectEntityDisplayName);

export const selectSelectedEntityLogo = createSelector(
  selectSelectedEntityDetails,
  (entityDetails) => entityDetails?.logo || null,
);

export const selectSelectedEntityName = createSelector(
  selectSelectedEntityDetails,
  (entityDetails) => entityDetails?.name || null,
);

export const selectEntityLogoByEntityId = createSelector(
  selectEntityDetailsByEntityId,
  (configDetails) => configDetails?.logo || null,
);

export const selectIsAiIconHiddenOnClipsPageByEntityId = createSelector(
  selectSelectedEntityId,
  (state, props) => props.page,
  (entityId, page) => AI_ICON_RESTRICTED_ENTITY_IDS?.includes(entityId) && page === APP_PAGE.CLIPS,
);

export const selectEntityAssignableUsers = createSelector(
  selectEntityDetailsByEntityId,
  (entityDetails) => entityDetails?.assignableUsers || [],
);

export const selectEntityTotalAssignableUsers = createSelector(
  selectEntityDetailsByEntityId,
  (entityDetails) => entityDetails?.totalAssignableUsers || 0,
);

export const selectCanViewOrganizationProfileSidebarByEntityRole = createSelector(
  selectSelectedEntityRole,
  (entityRole) => {
    return [ROLE.SUPER_ADMIN, ROLE.ADMIN, ROLE.USER, ROLE.REVIEWER, ...Object.values(cdRoles)].includes(entityRole);
  },
);

export const selectIsAdminOrCDRoles = createSelector(selectSelectedEntityRole, (entityRole) =>
  Object.values(cdAndAdminRoles).includes(entityRole),
);
